import { motion } from 'framer-motion'
import { CheckCircle, FileEdit, Eye, ThumbsUp, Globe } from 'lucide-react'

interface StatusPipelineProps {
  currentStatus: string
  onStatusChange?: (newStatus: string) => void
  disabled?: boolean
}

const STEPS = [
  { key: 'draft', label: 'Borrador', icon: FileEdit },
  { key: 'review', label: 'En Revisión', icon: Eye },
  { key: 'approved', label: 'Aprobado', icon: ThumbsUp },
  { key: 'published', label: 'Publicado', icon: Globe },
]

const statusColors: Record<string, { bg: string; border: string; text: string; line: string }> = {
  completed: {
    bg: 'bg-emerald-500',
    border: 'border-emerald-500',
    text: 'text-emerald-700',
    line: 'bg-emerald-500',
  },
  current: {
    bg: 'bg-emerald-500',
    border: 'border-emerald-500',
    text: 'text-emerald-700',
    line: 'bg-slate-200',
  },
  upcoming: {
    bg: 'bg-white',
    border: 'border-slate-300',
    text: 'text-slate-400',
    line: 'bg-slate-200',
  },
}

function getStepState(stepIndex: number, currentIndex: number) {
  if (stepIndex < currentIndex) return 'completed'
  if (stepIndex === currentIndex) return 'current'
  return 'upcoming'
}

export function StatusPipeline({ currentStatus, onStatusChange, disabled }: StatusPipelineProps) {
  const currentIndex = STEPS.findIndex((s) => s.key === currentStatus)
  const resolvedIndex = currentIndex === -1 ? 0 : currentIndex

  return (
    <div className="flex items-center w-full">
      {STEPS.map((step, index) => {
        const state = getStepState(index, resolvedIndex)
        const colors = statusColors[state]
        const Icon = step.icon
        const isClickable = !disabled && onStatusChange && index !== resolvedIndex

        return (
          <div key={step.key} className="flex items-center flex-1 last:flex-none">
            {/* Step circle + label */}
            <div className="flex flex-col items-center">
              <motion.button
                onClick={() => isClickable && onStatusChange(step.key)}
                disabled={!isClickable}
                className={`
                  relative w-10 h-10 rounded-full border-2 flex items-center justify-center
                  transition-all duration-200
                  ${colors.border} ${colors.bg}
                  ${isClickable ? 'cursor-pointer hover:scale-110 hover:shadow-md' : 'cursor-default'}
                `}
                whileTap={isClickable ? { scale: 0.95 } : undefined}
                title={isClickable ? `Cambiar a ${step.label}` : step.label}
              >
                {state === 'completed' ? (
                  <CheckCircle className="w-5 h-5 text-white" />
                ) : state === 'current' ? (
                  <Icon className="w-5 h-5 text-white" />
                ) : (
                  <Icon className="w-5 h-5 text-slate-400" />
                )}

                {/* Pulse animation for current step */}
                {state === 'current' && (
                  <motion.div
                    className="absolute inset-0 rounded-full border-2 border-emerald-400"
                    initial={{ scale: 1, opacity: 0.5 }}
                    animate={{ scale: 1.3, opacity: 0 }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                )}
              </motion.button>

              <span className={`mt-2 text-xs font-medium ${colors.text} whitespace-nowrap`}>
                {step.label}
              </span>
            </div>

            {/* Connector line */}
            {index < STEPS.length - 1 && (
              <div className="flex-1 h-0.5 mx-2 mt-[-20px] relative">
                <div className="absolute inset-0 bg-slate-200 rounded" />
                {index < resolvedIndex && (
                  <motion.div
                    className="absolute inset-0 bg-emerald-500 rounded"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    style={{ transformOrigin: 'left' }}
                  />
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
